import type {
  DrillDownCardElement,
  QueueCardElement,
  SopCardElement,
  TextBlockElement,
} from '../../../demo-elements/types'
import type { DemoChatScript } from '../../../types'

const EXCEPTION_QUEUE: QueueCardElement = {
  kind: 'queue-card',
  id: 'postship-exception-queue',
  title:
    'Cascade 3PL — Post-Shipment Monitor — 138,400 active shipments · 22 critical exceptions (48h)',
  kpis: [
    {
      label: 'Critical exceptions',
      value: '22',
      sub: '14 delayed · 6 lost · 2 damaged',
      tone: 'warning',
    },
    {
      label: 'Auto-handled this week',
      value: '285',
      sub: 'in-transit exceptions resolved by the agent (220 customer notices auto-sent)',
      tone: 'good',
    },
    {
      label: 'Escalated this week',
      value: '38',
      sub: 'high-value · $500+ · brand-wide pattern',
      tone: 'neutral',
    },
  ],
  columns: [
    { id: 'brand', label: 'Brand' },
    { id: 'customer', label: 'Customer', compact: true },
    { id: 'issue', label: 'Issue' },
    { id: 'carrier', label: 'Carrier', compact: true },
    { id: 'late', label: 'Late', align: 'right', compact: true },
  ],
  rows: [
    {
      id: 'exc-northwind-riley',
      status: 'critical',
      emphasize: true,
      cells: {
        brand: 'Northwind Apparel',
        customer: 'Riley K.',
        issue: 'Stuck in Cleveland',
        carrier: 'UPS',
        late: { value: '4d', tone: 'critical' },
      },
    },
    {
      id: 'exc-trailmix-marcus',
      status: 'critical',
      cells: {
        brand: 'Trailmix Provisions',
        customer: 'Marcus T.',
        issue: 'Lost · no scan 7d',
        carrier: 'USPS',
        late: { value: '7d', tone: 'critical' },
      },
    },
    {
      id: 'exc-oakhill-dana',
      status: 'warning',
      cells: {
        brand: 'OakHill Skincare',
        customer: 'Dana M.',
        issue: 'Delivered damaged',
        carrier: 'UPS',
        late: { value: '—', tone: 'neutral' },
      },
    },
    {
      id: 'exc-briar-emma',
      status: 'warning',
      cells: {
        brand: 'Briar Coffee Roasters',
        customer: 'Emma R.',
        issue: 'Stuck in Memphis',
        carrier: 'USPS',
        late: { value: '3d', tone: 'warning' },
      },
    },
    {
      id: 'exc-halcyon-tyler',
      status: 'warning',
      cells: {
        brand: 'Halcyon Pet',
        customer: 'Tyler S.',
        issue: 'Address hold',
        carrier: 'FedEx',
        late: { value: '1d', tone: 'warning' },
      },
    },
    {
      id: 'exc-altitude-priya',
      status: 'warning',
      cells: {
        brand: 'Altitude Supplements',
        customer: 'Priya N.',
        issue: 'Late guarantee missed',
        carrier: 'UPS',
        late: { value: '2d', tone: 'warning' },
      },
    },
  ],
  footer: 'Showing 6 of 22 critical exceptions, severity descending.',
}

const NORTHWIND_DRILL: DrillDownCardElement = {
  kind: 'drill-down-card',
  id: 'postship-northwind-drill',
  title: 'Northwind Apparel · Riley K. · UPS 1Z999AA10123456784',
  subtitle: 'Stuck at UPS Cleveland Hub · no scan in 4 days',
  facts: [
    { label: 'Last scan', value: 'Cleveland Hub · 4d ago', tone: 'critical' },
    { label: 'ETA', value: '2 days overdue', tone: 'critical' },
    { label: 'Order', value: '$124.50 · 3 items' },
    {
      label: 'Customer',
      value: 'Riley K. · 12 orders YTD · $2,400 LTV',
      tone: 'good',
    },
    {
      label: 'Brand signal',
      value: '6 similar tickets this week',
      tone: 'warning',
    },
  ],
  primaryAction: {
    label: 'Proactive customer notice',
    context: 'to riley.k@gmail.com · email',
    preview:
      "Hi Riley — heads up that your Northwind order has been moving slowly through UPS. We're tracking it closely and expect it within 2–3 days. If it doesn't arrive within 48 hours, we'll ship a replacement at no charge. Thanks for being patient with us.",
    accent: 'email',
  },
  supportingActions: [
    {
      text: 'Brand-side Slack to #cs-northwind (auto-sends on approve)',
      accent: 'slack',
    },
    {
      text: 'Replacement order pre-staged — fires in 48 hours if still undelivered',
      accent: 'order',
    },
  ],
}

const PREVENTION_METRIC: TextBlockElement = {
  kind: 'text-block',
  id: 'postship-prevention-metric',
  text: `**Cascade post-shipment outcomes — last 90 days:**
- 1,840 in-transit issues handled
- 1,420 customer notices sent before the customer reached out
- 290 replacement orders auto-triggered on carrier-confirmed loss
- 130 cases escalated (high-value · $500+ · brand-wide pattern)

_The other 420 cases: customer emailed first. **310 of those 420 were between day 2 and day 3 of no-scan** — just under the current 3-day threshold._`,
}

const SOP_CARD: SopCardElement = {
  kind: 'sop-card',
  id: 'postship-sop',
  title: 'Cascade · Post-Shipment SOP',
  sectionTitle: 'Customer notice for delayed shipments',
  sectionBody:
    'Send a proactive notice when the carrier shows no scan for **3+ days**. Offer a replacement at no charge if the package has not been delivered within 5 days of the original ETA. Loyal customers (>5 orders YTD) get the replacement pre-staged automatically.',
  editPreview: {
    before: 'No scan ≥ 3 days',
    after: 'No scan ≥ 2 days',
  },
}

const BEAT1_CLOSER: TextBlockElement = {
  kind: 'text-block',
  id: 'postship-beat1-closer',
  text: "22 critical in the last 48h, top one is Riley K. — 4 days into a Cleveland-hub stall. **Drill into Riley K.'s UPS shipment.**",
}

const BEAT2_CLOSER: TextBlockElement = {
  kind: 'text-block',
  id: 'postship-beat2-closer',
  text: 'Customer notice drafted. Brand-side Slack and replacement order auto-fire on approve.',
}

const BEAT3_CLOSER: TextBlockElement = {
  kind: 'text-block',
  id: 'postship-beat3-closer',
  text: '1,840 issues, 1,420 same-day notices, 130 escalations — 90-day window. **Want to see how the rules are configured?**',
}

/**
 * Cascade 3PL — Demo 3: Post-Shipment Support Agent.
 *
 * Beat 1: Post-Shipment Monitor. 22 critical exceptions in the last 48h
 * (out of 138,400 active shipments). Riley K. (Northwind UPS Cleveland 4d)
 * sits at the top.
 *
 * Beat 2: Drill into Riley's exception. Tracking, customer value (12 YTD
 * orders, $2,400 LTV), brand context. Three pre-staged actions — customer
 * notice, brand-side Slack, replacement order. Approve the customer notice.
 *
 * Beat 3: 90-day prevention metric. 1,420 customer questions never asked.
 *
 * Beat 4: SOP edit moment. Cascade SOP in Google Drive. Approve changing
 * the no-scan threshold from 3 days to 2 days.
 */
export const postShipmentSupport: DemoChatScript = {
  id: 'post-shipment-support',
  name: 'Post-Shipment Support Agent',
  description:
    'Demo 3. Cascade watches every in-transit shipment across 4 facilities, catches delays before the customer (or the brand) does, drafts the response, and asks for one click.',
  triggers: [
    'post-shipment',
    'post shipment',
    'post shipment monitor',
    'shipment monitor',
    'monitor',
    'customer service',
    'customer comms',
    'customer notice',
    'delay notifications',
    'delays',
    'carrier delays',
    'lost orders',
    'lost order',
    'stuck shipment',
    'stuck',
    'in transit',
    'exceptions',
    'exception queue',
    'tracking exceptions',
    'where is my order',
    "where's my order",
    'wismo',
    'riley',
    'riley k',
    'northwind',
    'northwind apparel',
    'ups',
    'cleveland',
  ],
  followUps: [
    {
      triggers: [
        'yes',
        'yeah',
        'yep',
        'sure',
        'ok',
        'okay',
        'go',
        'go ahead',
        'do it',
        'please',
        'continue',
        'next',
        "what's next",
        'whats next',
        'show me',
        'show me more',
        'more',
        'keep going',
        'claims',
        'carrier claims',
        'money',
        'recovery',
        'file claims',
      ],
      nextScriptId: 'carrier-claims-agent',
    },
    {
      triggers: ['agents', 'bulk ship', 'morning', 'labels'],
      nextScriptId: 'bulk-label-agent',
    },
  ],
  events: [
    // ─────────── Beat 1: Exception queue ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Loading post-shipment exceptions...',
    },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        "138,400 active shipments across UPS, USPS, FedEx, OnTrac. 22 critical exceptions in the last 48h — the UPS-Cleveland delay on Riley K.'s order is the freshest critical.\n",
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'list_shipment_exceptions' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta: '22 critical exceptions in the last 48h, sorted by severity.',
    },
    { type: 'demo-element-add', delayMs: 600, element: EXCEPTION_QUEUE },
    { type: 'demo-element-add', delayMs: 700, element: BEAT1_CLOSER },
    { type: 'done', delayMs: 400, title: 'Post-Shipment Monitor — exceptions' },
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 2: Riley K. drill-down + approval ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Pulling tracking, customer history, brand context...',
    },
    { type: 'tool-call', delayMs: 300, name: 'fetch_shipment_detail' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta:
        'UPS shipment for Riley K., 4 days at Cleveland hub. Notice drafted.',
    },
    { type: 'demo-element-add', delayMs: 600, element: NORTHWIND_DRILL },
    {
      type: 'approval',
      delayMs: 700,
      proposal: {
        proposalId: 'demo-cascade-postship-send-customer',
        toolName: 'send_customer_notice',
        actionType: 'customer_communication',
        description:
          'Send proactive notice to Riley K. (Northwind Apparel · UPS 1Z999AA10123456784): "UPS hub delay, expected within 2-3 days, replacement in 48 hours if not delivered." On approve: brand-side Slack to #cs-northwind auto-fires; replacement order pre-staged to auto-fire in 48 hours if still undelivered.',
      },
    },
    { type: 'demo-element-add', delayMs: 600, element: BEAT2_CLOSER },
    { type: 'done', delayMs: 400, title: 'Riley K. — notice sent' },
    {
      type: 'await-approval-resolved',
      delayMs: 0,
      proposalId: 'demo-cascade-postship-send-customer',
    },

    // ─────────── Beat 3: Prevention metric (auto-fires after approval) ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Rolling up the last 90 days...',
    },
    { type: 'tool-call', delayMs: 300, name: 'summarize_agent_outcomes' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta: 'Notice sent · Slack posted · replacement queued. 90-day rollup:',
    },
    { type: 'demo-element-add', delayMs: 600, element: PREVENTION_METRIC },
    { type: 'demo-element-add', delayMs: 700, element: BEAT3_CLOSER },
    { type: 'done', delayMs: 400, title: 'Post-shipment — 90-day outcomes' },
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 4: SOP edit ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Opening the Cascade post-shipment SOP...',
    },
    { type: 'tool-call', delayMs: 300, name: 'fetch_sop' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta:
        'That day-2-to-3 cluster — moving the trigger from 3 days to 2 catches those 310 customers per quarter before they email. Same rule, Cascade SOP in Drive.',
    },
    { type: 'demo-element-add', delayMs: 600, element: SOP_CARD },
    {
      type: 'approval',
      delayMs: 700,
      proposal: {
        proposalId: 'demo-cascade-postship-sop-edit',
        toolName: 'update_sop',
        actionType: 'sop_update',
        description:
          'Update Cascade post-shipment SOP: customer-notice trigger from "no scan ≥ 3 days" to "no scan ≥ 2 days." Recovers ~310 of the 420 cases per quarter where the customer currently emails first. Next monitor pass picks up the new threshold; no code change, no ticket.',
      },
    },
    { type: 'done', delayMs: 400, title: 'SOP — 2-day threshold' },
    {
      type: 'await-approval-resolved',
      delayMs: 0,
      proposalId: 'demo-cascade-postship-sop-edit',
    },

    // ─────────── Wrap: cue toward the carrier claims demo ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'SOP update logged.',
    },
    {
      type: 'text-delta',
      delayMs: 400,
      delta: 'New threshold live on the next monitor pass.',
    },
    {
      type: 'demo-element-add',
      delayMs: 500,
      element: {
        kind: 'text-block',
        id: 'postship-wrap',
        text: '**Want to see how carrier claims looks next?**',
      },
    },
    { type: 'done', delayMs: 400, title: 'Post-shipment demo — complete' },
  ],
}
